/**
 * 基于 fetch 的网络请求工具
 * 支持多后端实例，非单例模式
 */

export interface RequestConfig extends Omit<RequestInit, "body"> {
  baseURL?: string;
  timeout?: number;
  params?: Record<
    string,
    | string
    | number
    | boolean
    | undefined
    | null
    | Array<string | number | boolean>
    | ReadonlyArray<string | number | boolean>
  >;
  data?: unknown;
  responseType?: "json" | "text" | "blob" | "arrayBuffer";
  manualErrorHandling?: boolean;
}

export interface DownloadConfig extends RequestConfig {
  filename?: string;
}

export interface DownloadResult {
  blob: Blob;
  filename: string;
  /** 手动触发下载 */
  save: () => void;
}

interface RequestExecutionResult<T> {
  response: Response;
  data: T;
}

export type RequestInterceptor = (config: RequestConfig) => RequestConfig | Promise<RequestConfig>;
export type ResponseInterceptor<T = unknown> = (response: Response, data: T) => T | Promise<T>;
export type ErrorInterceptor = (error: RequestError) => void | Promise<void>;

export interface RequestInstance {
  <T = unknown>(url: string, config?: RequestConfig): Promise<T>;
  get: <T = unknown>(url: string, config?: RequestConfig) => Promise<T>;
  post: <T = unknown>(url: string, data?: unknown, config?: RequestConfig) => Promise<T>;
  put: <T = unknown>(url: string, data?: unknown, config?: RequestConfig) => Promise<T>;
  patch: <T = unknown>(url: string, data?: unknown, config?: RequestConfig) => Promise<T>;
  delete: <T = unknown>(url: string, config?: RequestConfig) => Promise<T>;
  /** 获取 Blob 并自动触发下载 */
  download: (url: string, config?: DownloadConfig) => Promise<void>;
  /** 获取 Blob 并自动触发下载 (POST) */
  downloadPost: (url: string, data?: unknown, config?: DownloadConfig) => Promise<void>;
  /** 获取 Blob，返回 DownloadResult，可手动调用 save() 下载 */
  getBlob: (url: string, config?: DownloadConfig) => Promise<DownloadResult>;
  /** 获取 Blob (POST)，返回 DownloadResult，可手动调用 save() 下载 */
  getBlobPost: (url: string, data?: unknown, config?: DownloadConfig) => Promise<DownloadResult>;
  /** 添加请求拦截器，返回移除函数 */
  addRequestInterceptor: (interceptor: RequestInterceptor) => () => void;
  /** 添加响应拦截器，返回移除函数 */
  addResponseInterceptor: (interceptor: ResponseInterceptor) => () => void;
  /** 添加错误拦截器，返回移除函数 */
  addErrorInterceptor: (interceptor: ErrorInterceptor) => () => void;
}

export interface CreateRequestOptions {
  baseURL?: string;
  timeout?: number;
  headers?: HeadersInit;
}

export type UnauthorizedHandler = (error: RequestError) => void;
export type TokenProvider = () => string | null;
export type TenantCodeProvider = () => string | null;
export type IOSDialogHandler = (options: {
  title?: string;
  content: string;
  okText?: string;
  onOk?: () => void | Promise<void>;
  showCancel?: boolean;
}) => void;
type MessageApi = { error?: (content: any) => void };
type ModalApi = {
  error?: (config: {
    title?: string;
    content: string;
    okText?: string;
    onOk?: () => void | Promise<void>;
    afterClose?: () => void;
  }) => void;
};

let unauthorizedHandler: UnauthorizedHandler | null = null;
let tokenProvider: TokenProvider | null = null;
let tenantCodeProvider: TenantCodeProvider | null = null;
let messageApi: MessageApi | null = null;
let iosDialogHandler: IOSDialogHandler | null = null;
let modalApi: ModalApi | null = null;

// 错误消息防重复弹窗缓存
const errorMessageCache = new Map<string, number>();
const ERROR_DUPLICATE_WINDOW = 3000; // 3秒内相同错误不重复显示

// 防止重复执行登出逻辑
let isHandlingLogout = false;

export function setUnauthorizedHandler(handler: UnauthorizedHandler | null) {
  unauthorizedHandler = handler;
}

export function setTokenProvider(provider: TokenProvider | null) {
  tokenProvider = provider;
}

export function setTenantCodeProvider(provider: TenantCodeProvider | null) {
  tenantCodeProvider = provider;
}

/** 注入 UI 层的 message 实例，避免工具层直接依赖全局 message */
export function setMessageApi(api: MessageApi | null) {
  messageApi = api;
}

export function setIOSDialogHandler(handler: IOSDialogHandler | null) {
  iosDialogHandler = handler;
}

/** 注入 UI 层的 modal 实例，用于显示错误弹窗 */
export function setModalApi(api: ModalApi | null) {
  modalApi = api;
}

export async function showBlockingError(options: {
  title?: string;
  content: string;
  okText?: string;
  onOk?: () => void | Promise<void>;
}): Promise<void> {
  const { title = "登入失敗", content, okText = "確定", onOk } = options;

  if (iosDialogHandler) {
    const handler = iosDialogHandler;
    await new Promise<void>((resolve) => {
      handler({
        title,
        content,
        okText,
        showCancel: false,
        onOk: async () => {
          await Promise.resolve(onOk?.());
          resolve();
        },
      });
    });
    return;
  }

  if (modalApi?.error) {
    const modalError = modalApi.error;
    await new Promise<void>((resolve) => {
      let finished = false;
      const finish = () => {
        if (finished) return;
        finished = true;
        resolve();
      };

      modalError({
        title,
        content,
        okText,
        onOk: async () => {
          await Promise.resolve(onOk?.());
          finish();
        },
        afterClose: finish,
      });
    });
    return;
  }

  if (typeof window !== "undefined" && typeof window.alert === "function") {
    window.alert(content);
    await Promise.resolve(onOk?.());
    return;
  }

  notifyError(content);
}

function notifyError(content: any) {
  const message = String(content);
  const now = Date.now();
  const lastShown = errorMessageCache.get(message);

  // 如果在指定时间窗口内已经显示过相同消息，则跳过
  if (lastShown && now - lastShown < ERROR_DUPLICATE_WINDOW) {
    return;
  }

  // 记录本次显示时间
  errorMessageCache.set(message, now);

  // 清理过期的缓存（保持 Map 大小合理）
  if (errorMessageCache.size > 50) {
    const cutoff = now - ERROR_DUPLICATE_WINDOW;
    for (const [msg, timestamp] of errorMessageCache.entries()) {
      if (timestamp < cutoff) {
        errorMessageCache.delete(msg);
      }
    }
  }

  if (messageApi?.error) {
    messageApi.error(content);
  } else {
    console.error(content);
  }
}

function resolveToken(): string | null {
  if (tokenProvider) {
    const token = tokenProvider();
    if (token) return token;
  }

  if (typeof localStorage === "undefined") return null;

  // 优先读取直接存储的 token
  const directToken = localStorage.getItem("token");
  if (directToken) return directToken;

  // 尝试读取默认持久化 key（ifa-admin-store）中的 token
  const persisted = localStorage.getItem("ifa-admin-store");
  if (persisted) {
    try {
      const parsed = JSON.parse(persisted);
      if (parsed?.state?.token) return parsed.state.token;
    } catch {
      // ignore parse errors
    }
  }

  return null;
}

function resolveTenantCode(): string | null {
  if (tenantCodeProvider) {
    const code = tenantCodeProvider();
    if (code) return code;
  }

  if (typeof localStorage === "undefined") return null;

  const directTenant = localStorage.getItem("tenantCode");
  if (directTenant) return directTenant;

  const persisted = localStorage.getItem("ifa-admin-store");
  if (persisted) {
    try {
      const parsed = JSON.parse(persisted);
      if (parsed?.state?.tenantCode) return parsed.state.tenantCode as string;
    } catch {
      // ignore parse errors
    }
  }

  return null;
}

export class RequestError extends Error {
  status: number | undefined;
  statusText: string | undefined;
  response: Response | undefined;
  data: unknown;
  config: RequestConfig | undefined;

  constructor(
    message: string,
    options?: {
      status?: number;
      statusText?: string;
      response?: Response;
      data?: unknown;
      config?: RequestConfig;
    },
  ) {
    super(message);
    this.name = "RequestError";
    this.status = options?.status;
    this.statusText = options?.statusText;
    this.response = options?.response;
    this.data = options?.data;
    this.config = options?.config;
  }
}

export function hasUnauthorizedHandler(): boolean {
  return unauthorizedHandler !== null;
}

export function dispatchUnauthorized(error: RequestError): boolean {
  if (!unauthorizedHandler) {
    return false;
  }

  unauthorizedHandler(error);
  return true;
}

function runForcedLogoutFallback() {
  if (typeof localStorage !== "undefined") {
    localStorage.removeItem("token");
    localStorage.removeItem("userInfo");
    localStorage.removeItem("uid");
    localStorage.removeItem("tenantCode");
  }

  if (typeof window !== "undefined" && window.location.pathname !== "/") {
    window.location.href = "/";
  }
}

function handleForcedLogout(error: RequestError) {
  if (dispatchUnauthorized(error)) {
    return;
  }

  runForcedLogoutFallback();
}

/**
 * 从 Content-Disposition 头解析文件名
 */
function parseFilenameFromHeader(contentDisposition: string | null): string | null {
  if (!contentDisposition) return null;

  const decodeFilename = (value: string): string => {
    const trimmed = value.trim().replace(/^["']|["']$/g, "");
    try {
      return decodeURIComponent(trimmed);
    } catch {
      return trimmed;
    }
  };

  // 尝试解析 filename*=UTF-8''xxx 格式
  const utf8Match = contentDisposition.match(/filename\*=UTF-8''([^;]+)/i);
  if (utf8Match?.[1]) {
    return decodeFilename(utf8Match[1]);
  }

  // 尝试解析 filename="xxx" 格式
  const quotedMatch = contentDisposition.match(/filename="([^"]+)"/i);
  if (quotedMatch?.[1]) {
    return decodeFilename(quotedMatch[1]);
  }

  // 尝试解析 filename=xxx 格式
  const plainMatch = contentDisposition.match(/filename=([^;\s]+)/i);
  if (plainMatch?.[1]) {
    return decodeFilename(plainMatch[1]);
  }

  return null;
}

/**
 * 从 URL 解析文件名
 */
function parseFilenameFromURL(url: string): string {
  const pathname = new URL(url, "http://localhost").pathname;
  const filename = pathname.split("/").pop();
  return filename ?? "download";
}

/**
 * 触发浏览器下载
 */
function triggerDownload(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  link.style.display = "none";
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

/**
 * 构建 URL 参数
 */
function buildURL(url: string, params?: RequestConfig["params"]): string {
  if (!params) return url;

  const searchParams = new URLSearchParams();
  Object.entries(params).forEach(([key, value]) => {
    if (value === undefined || value === null) {
      return;
    }

    if (Array.isArray(value)) {
      value.forEach((item) => {
        if (item !== undefined && item !== null) {
          searchParams.append(key, String(item));
        }
      });
      return;
    }

    searchParams.append(key, String(value));
  });

  const queryString = searchParams.toString();
  if (!queryString) return url;

  return url.includes("?") ? `${url}&${queryString}` : `${url}?${queryString}`;
}

/**
 * 合并 headers
 */
function mergeHeaders(defaultHeaders?: HeadersInit, configHeaders?: HeadersInit): Headers {
  const headers = new Headers(defaultHeaders);
  if (configHeaders) {
    const additional = new Headers(configHeaders);
    additional.forEach((value, key) => {
      headers.set(key, value);
    });
  }
  return headers;
}

/**
 * 创建请求实例
 */
export function createRequest(options: CreateRequestOptions = {}): RequestInstance {
  const { baseURL = "", timeout = 30000, headers: defaultHeaders } = options;

  // 拦截器数组
  const requestInterceptors: RequestInterceptor[] = [];
  const responseInterceptors: ResponseInterceptor[] = [];
  const errorInterceptors: ErrorInterceptor[] = [];

  // 检查业务错误码：账号已在别处登录
  // @ts-ignore
  const checkAndHandleLogout = (data: any) => {
    if (data && typeof data === "object" && (data as any).code === "client.already.logout") {
      // 防止多个接口同时返回此错误时重复执行登出逻辑
      if (isHandlingLogout) {
        return;
      }
      isHandlingLogout = true;

      const msg = (data as any).message || "您的帳號在其他地方登入，當前登入已被註銷。";
      notifyError(msg);
      const error = new RequestError(msg, {
        status: 401,
        data,
      });

      handleForcedLogout(error);
      setTimeout(() => {
        isHandlingLogout = false;
      }, 1000);

      throw error;
    }
  };

  async function executeRequest<T = unknown>(
    url: string,
    config: RequestConfig = {},
  ): Promise<RequestExecutionResult<T>> {
    // 合并配置，动态注入 token
    const token = resolveToken();
    const tenantCode = resolveTenantCode();
    const builtinHeaders: HeadersInit = {
      ...(tenantCode ? { "x-bee-tenant": tenantCode } : { "x-bee-tenant": "dev" }),
      ...(token ? { Authorization: `${token}` } : {}),
    };

    let mergedConfig: RequestConfig = {
      ...config,
      baseURL: config.baseURL ?? baseURL,
      timeout: config.timeout ?? timeout,
      headers: mergeHeaders(mergeHeaders(builtinHeaders, defaultHeaders), config.headers),
    };

    // 执行请求拦截器链
    for (const interceptor of requestInterceptors) {
      mergedConfig = await interceptor(mergedConfig);
    }

    // 构建完整 URL
    const fullURL = buildURL(mergedConfig.baseURL ? `${mergedConfig.baseURL.replace(/\/$/, "")}/${url.replace(/^\//, "")}` : url, mergedConfig.params);

    // 构建请求配置，只添加有值的属性
    const fetchConfig: RequestInit = {
      method: mergedConfig.method ?? "GET",
    };

    if (mergedConfig.headers !== undefined) {
      fetchConfig.headers = mergedConfig.headers;
    }

    if (mergedConfig.credentials !== undefined) {
      fetchConfig.credentials = mergedConfig.credentials;
    }
    if (mergedConfig.mode !== undefined) {
      fetchConfig.mode = mergedConfig.mode;
    }
    if (mergedConfig.cache !== undefined) {
      fetchConfig.cache = mergedConfig.cache;
    }
    if (mergedConfig.redirect !== undefined) {
      fetchConfig.redirect = mergedConfig.redirect;
    }
    if (mergedConfig.referrer !== undefined) {
      fetchConfig.referrer = mergedConfig.referrer;
    }
    if (mergedConfig.referrerPolicy !== undefined) {
      fetchConfig.referrerPolicy = mergedConfig.referrerPolicy;
    }
    if (mergedConfig.integrity !== undefined) {
      fetchConfig.integrity = mergedConfig.integrity;
    }

    // 处理请求体
    if (mergedConfig.data !== undefined) {
      const headers = mergedConfig.headers as Headers;
      const contentType = headers.get("Content-Type");

      if (mergedConfig.data instanceof FormData) {
        fetchConfig.body = mergedConfig.data;
        // FormData 需要让浏览器自动设置 Content-Type
        headers.delete("Content-Type");
      } else if (mergedConfig.data instanceof URLSearchParams) {
        fetchConfig.body = mergedConfig.data;
        if (!contentType) {
          headers.set("Content-Type", "application/x-www-form-urlencoded");
        }
      } else if (typeof mergedConfig.data === "object") {
        fetchConfig.body = JSON.stringify(mergedConfig.data);
        if (!contentType) {
          headers.set("Content-Type", "application/json");
        }
      } else {
        fetchConfig.body = String(mergedConfig.data);
      }
    }

    // 超时处理
    const controller = new AbortController();
    const timeoutMs = mergedConfig.timeout ?? timeout;
    const timeoutId = setTimeout(() => controller.abort(), timeoutMs);

    if (mergedConfig.signal) {
      // 如果用户提供了 signal，需要将其与内部 controller 关联
      if (mergedConfig.signal.aborted) {
        controller.abort();
      } else {
        mergedConfig.signal.addEventListener("abort", () => {
          controller.abort();
        });
      }
    }
    fetchConfig.signal = controller.signal;

    try {
      const response = await fetch(fullURL, fetchConfig);
      clearTimeout(timeoutId);

      // 解析响应
      let data: T;
      const responseType = mergedConfig.responseType ?? "json";

      try {
        switch (responseType) {
          case "text":
            data = (await response.text()) as T;
            break;
          case "blob":
            data = (await response.blob()) as T;
            break;
          case "arrayBuffer":
            data = (await response.arrayBuffer()) as T;
            break;
          case "json":
          default: {
            // 根据 Content-Type 自动判断
            const contentType = response.headers.get("Content-Type");
            if (contentType?.includes("application/json")) {
              data = (await response.json()) as T;
            } else {
              const text = await response.text();
              data = (text ? JSON.parse(text) : null) as T;
            }
            break;
          }
        }
      } catch {
        data = null as T;
      }

      // 检查响应状态
      if (!response.ok) {
        // 优先检查是否需要强制登出
        checkAndHandleLogout(data);

        const error = new RequestError((data as any)?.message || (data as any)?.msg || `Request failed with status ${response.status}`, {
          status: response.status,
          statusText: response.statusText,
          response,
          data,
          config: mergedConfig,
        });

        // 执行错误拦截器链
        for (const interceptor of errorInterceptors) {
          await interceptor(error);
        }
        throw error;
      }

      // 检查业务错误码：账号已在别处登录（需优先于通用业务错误处理）
      checkAndHandleLogout(data);

      // 执行响应拦截器链
      for (const interceptor of responseInterceptors) {
        data = (await interceptor(response, data)) as T;
      }

      // 默认解包 data 字段
      if (data && typeof data === "object" && "data" in (data as any)) {
        return {
          response,
          data: (data as any).data as T,
        };
      }
      return { response, data };
    } catch (error) {
      clearTimeout(timeoutId);

      if (error instanceof RequestError) {
        throw error;
      }

      let requestError: RequestError;
      if (error instanceof Error) {
        if (error.name === "AbortError") {
          // 如果用户提供了 signal，且 signal 已经被中止，说明是用户主动取消
          if (mergedConfig.signal?.aborted) {
            requestError = new RequestError("Canceled", { config: mergedConfig });
          } else {
            // 否则是内部 timeout 触发的 abort
            requestError = new RequestError("Request timeout", { config: mergedConfig });
          }
        } else {
          requestError = new RequestError(error.message, { config: mergedConfig });
        }
      } else {
        requestError = new RequestError("Unknown error", { config: mergedConfig });
      }

      // 执行错误拦截器链
      for (const interceptor of errorInterceptors) {
        await interceptor(requestError);
      }

      throw requestError;
    }
  }

  async function request<T = unknown>(url: string, config: RequestConfig = {}): Promise<T> {
    const result = await executeRequest<T>(url, config);
    return result.data;
  }

  // 添加便捷方法
  request.get = <T = unknown>(url: string, config?: RequestConfig) => request<T>(url, { ...config, method: "GET" });

  request.post = <T = unknown>(url: string, data?: unknown, config?: RequestConfig) => request<T>(url, { ...config, method: "POST", data });

  request.put = <T = unknown>(url: string, data?: unknown, config?: RequestConfig) => request<T>(url, { ...config, method: "PUT", data });

  request.patch = <T = unknown>(url: string, data?: unknown, config?: RequestConfig) => request<T>(url, { ...config, method: "PATCH", data });

  request.delete = <T = unknown>(url: string, data?: unknown, config?: RequestConfig) => request<T>(url, { ...config, method: "DELETE", data });

  // 获取 Blob 核心方法
  async function fetchBlob(url: string, method: "GET" | "POST", data: unknown, config?: DownloadConfig): Promise<DownloadResult> {
    const result = await executeRequest<Blob>(url, {
      ...config,
      method,
      data: method === "POST" ? data : undefined,
      responseType: "blob",
    });

    const contentDisposition = result.response.headers.get("content-disposition");
    const filename =
      parseFilenameFromHeader(contentDisposition) ??
      config?.filename ??
      parseFilenameFromURL(url);

    return {
      blob: result.data,
      filename,
      save: () => triggerDownload(result.data, filename),
    };
  }

  // 获取 Blob (GET)
  request.getBlob = (url: string, config?: DownloadConfig) => fetchBlob(url, "GET", undefined, config);

  // 获取 Blob (POST)
  request.getBlobPost = (url: string, data?: unknown, config?: DownloadConfig) => fetchBlob(url, "POST", data, config);

  // 下载文件 (GET) - 自动触发下载
  request.download = async (url: string, config?: DownloadConfig): Promise<void> => {
    const result = await fetchBlob(url, "GET", undefined, config);
    result.save();
  };

  // 下载文件 (POST) - 自动触发下载
  request.downloadPost = async (url: string, data?: unknown, config?: DownloadConfig): Promise<void> => {
    const result = await fetchBlob(url, "POST", data, config);
    result.save();
  };

  // 添加拦截器方法，返回移除函数
  request.addRequestInterceptor = (interceptor: RequestInterceptor) => {
    requestInterceptors.push(interceptor);
    return () => {
      const index = requestInterceptors.indexOf(interceptor);
      if (index > -1) {
        requestInterceptors.splice(index, 1);
      }
    };
  };

  request.addResponseInterceptor = (interceptor: ResponseInterceptor) => {
    responseInterceptors.push(interceptor);
    return () => {
      const index = responseInterceptors.indexOf(interceptor);
      if (index > -1) {
        responseInterceptors.splice(index, 1);
      }
    };
  };

  request.addErrorInterceptor = (interceptor: ErrorInterceptor) => {
    errorInterceptors.push(interceptor);
    return () => {
      const index = errorInterceptors.indexOf(interceptor);
      if (index > -1) {
        errorInterceptors.splice(index, 1);
      }
    };
  };

  // 内置响应拦截：当后端返回自定义错误码时统一抛出错误并带上 message
  request.addResponseInterceptor((response, data) => {
    const successCodes = new Set([0, 200, "ok", "OK"]);
    if (data && typeof data === "object" && "code" in (data as any) && !successCodes.has((data as any).code as any)) {
      const message = (data as any).message || (data as any).msg || "接口返回錯誤";
      const error = new RequestError(message, {
        status: response.status,
        statusText: response.statusText,
        response,
        data,
      });
      throw error;
    }
    return data;
  });

  // 内置错误拦截：统一输出 message，便于上层展示；401 时触发项目级处理
  request.addErrorInterceptor((error) => {
    const manualErrorHandling = error.config?.manualErrorHandling === true;
    const isUnauthorized = error.status === 401 || (error.data as any)?.code === 401 || (error.data as any)?.code === "401";
    if (!manualErrorHandling && isUnauthorized) {
      dispatchUnauthorized(error);
    }

    if (manualErrorHandling) return;

    const msg = (error.data as any)?.message || (error.data as any)?.msg || error.message;
    // 忽略手动取消的错误提示
    if (msg === "Canceled") return;

    const errorCode = (error.data as any)?.code;

    // 特定错误码使用 iOS 原生弹窗
    if (errorCode === "system.client.params.required" && iosDialogHandler) {
      iosDialogHandler({
        title: "錯誤提示",
        content: msg,
        okText: "確定",
        showCancel: false,
      });
      return;
    }

    // 需要弹窗显示的错误码列表
    const modalErrorCodes = new Set([
      "policyholder.age.invalid",
      "phone.invalid",
      "email.invalid",
      "id.number.invalid",
      "family.member.duplicate.relationship",
      "beneficiary.identity.error",
    ]);

    // 如果错误码在弹窗列表中，使用 iOS 确认对话框显示
    if (errorCode && modalErrorCodes.has(errorCode) && iosDialogHandler) {
      iosDialogHandler({
        title: "錯誤提示",
        content: msg,
        okText: "確定",
        showCancel: false,
      });
      return;
    }

    // 其他错误使用 message 提示
    if (msg) notifyError(msg);
    else console.error(error);
  });

  return request as RequestInstance;
}
